import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { VisitOutcome } from "./models/VisitOutcome";
import {
  fetchSessionAttendance,
  saveSessionAttendance,
  fetchAttendanceBatch,
} from "./slice-extensions/visits-slice-extension";

/** memberId -> recorded outcome, for a single session occurrence. */
export type SessionVisits = Record<string, VisitOutcome>;

/** Saved attendance for one occurrence: outcomes plus which members are one-off. */
export type OccurrenceAttendance = {
  outcomes: SessionVisits;
  oneOffMemberIds: string[];
  /** Member id → the Visit's own Payment Type id (e.g. a late-cancellation fee), when set. */
  paymentTypeIds?: Record<string, string>;
  /** The instructor cancelled this date — the screen offers no record action. */
  cancelled?: boolean;
  /** When the date was cancelled (ISO UTC), when known. */
  cancelledAt?: string | null;
};

/**
 * Attendance is recorded per occurrence — a recurring session has independent
 * attendance on each of its dates, so entries are keyed by session + date.
 */
export const visitsKey = (sessionId: string, occurrenceDate: string): string =>
  `${sessionId}|${occurrenceDate}`;

interface VisitsState {
  byOccurrence: Record<string, OccurrenceAttendance>;
}

const initialState: VisitsState = {
  byOccurrence: {},
};

const visitsSlice = createSlice({
  name: "visits",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const store = (
      state: VisitsState,
      action: PayloadAction<{ key: string; attendance: OccurrenceAttendance }>
    ) => {
      state.byOccurrence[action.payload.key] = action.payload.attendance;
    };

    builder.addCase(fetchSessionAttendance.fulfilled, store);
    builder.addCase(saveSessionAttendance.fulfilled, store);
    builder.addCase(fetchAttendanceBatch.fulfilled, (state, action) => {
      Object.assign(state.byOccurrence, action.payload);
    });
  },
});

const EMPTY: SessionVisits = {};
const EMPTY_ONE_OFFS: string[] = [];

export const selectSessionVisits =
  (sessionId: string, occurrenceDate: string) =>
  (state: { visits: VisitsState }): SessionVisits =>
    state.visits.byOccurrence[visitsKey(sessionId, occurrenceDate)]?.outcomes ?? EMPTY;

export const selectOccurrenceOneOffMembers =
  (sessionId: string, occurrenceDate: string) =>
  (state: { visits: VisitsState }): string[] =>
    state.visits.byOccurrence[visitsKey(sessionId, occurrenceDate)]?.oneOffMemberIds ??
    EMPTY_ONE_OFFS;

const EMPTY_PAYMENT_TYPES: Record<string, string> = {};

export const selectOccurrencePaymentTypeIds =
  (sessionId: string, occurrenceDate: string) =>
  (state: { visits: VisitsState }): Record<string, string> =>
    state.visits.byOccurrence[visitsKey(sessionId, occurrenceDate)]?.paymentTypeIds ??
    EMPTY_PAYMENT_TYPES;

/** Whether attendance for this date has arrived (until then the panel offers no action). */
export const selectOccurrenceLoaded =
  (sessionId: string, occurrenceDate: string) =>
  (state: { visits: VisitsState }): boolean =>
    visitsKey(sessionId, occurrenceDate) in state.visits.byOccurrence;

/** Whether the loaded attendance says the instructor cancelled this date. */
export const selectOccurrenceCancelled =
  (sessionId: string, occurrenceDate: string) =>
  (state: { visits: VisitsState }): boolean =>
    state.visits.byOccurrence[visitsKey(sessionId, occurrenceDate)]?.cancelled ?? false;

/** All loaded attendance keyed by `${sessionId}|${occurrenceDate}`. */
export const selectAllSessionVisits = (state: {
  visits: VisitsState;
}): Record<string, OccurrenceAttendance> => state.visits.byOccurrence;

export default visitsSlice.reducer;
